/**
 * B403 wave-2 residual — the storefront-wide TIME REGISTER, one module.
 *
 * The frames (72:2 tickets, 73:2 cart, 74:2 done, 58:238 widget) speak a
 * 24-HOUR clock ("07:00", "10:30", "16:30") and humane day words
 * ("Fri 15", the confirmation's "Fri 15 Aug") — the deployed build spoke
 * the locale's default 12-hour face ("10:30 AM") and a locale dump on
 * cart lines ("Wed, Aug 26, 12:30 PM"). One derivation cures the tickets
 * pills, the departure pills, the cart lines and the picks line together;
 * every consumer imports these words rather than minting an
 * Intl.DateTimeFormat of its own.
 *
 * The locale still owns the WORDS (weekday and month names localize);
 * the register owns the SHAPE (h23 clock, "<Day> <n>[ <Mon>] · <hh:mm>").
 */

export interface WhenOptions {
  readonly locale?: string;
  readonly timeZone?: string;
}

const tzOf = (opts: WhenOptions): { timeZone?: string } =>
  opts.timeZone !== undefined ? { timeZone: opts.timeZone } : {};

/** "10:30", "07:00" — the frames' 24h clock, whatever the locale's habit. */
export function clockWords(ms: number, opts: WhenOptions = {}): string {
  return new Intl.DateTimeFormat(opts.locale, {
    ...tzOf(opts),
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(ms);
}

/** "Fri 15" — composed, so no locale reorders the frame's shape. */
export function dayWords(ms: number, opts: WhenOptions = {}): string {
  const weekday = new Intl.DateTimeFormat(opts.locale, { ...tzOf(opts), weekday: "short" }).format(ms);
  const day = new Intl.DateTimeFormat(opts.locale, { ...tzOf(opts), day: "numeric" }).format(ms);
  return `${weekday} ${day}`;
}

/** "Fri 15 Aug" — the confirmation's fuller day, month word included. */
export function dayMonthWords(ms: number, opts: WhenOptions = {}): string {
  const month = new Intl.DateTimeFormat(opts.locale, { ...tzOf(opts), month: "short" }).format(ms);
  return `${dayWords(ms, opts)} ${month}`;
}

/** "Fri 15 · 10:30" — the in-flow departure face (cart lines, picks line). */
export function slotWords(ms: number, opts: WhenOptions = {}): string {
  return `${dayWords(ms, opts)} · ${clockWords(ms, opts)}`;
}

/** "Fri 15 Aug · 10:30" — the confirmation summary's departure face (74:2). */
export function slotFullWords(ms: number, opts: WhenOptions = {}): string {
  return `${dayMonthWords(ms, opts)} · ${clockWords(ms, opts)}`;
}
